import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { I18nProvider } from "@/i18n/I18nProvider";
import { Nav } from "@/components/festival/Nav";
import { Footer } from "@/components/festival/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { z } from "zod";

export const Route = createFileRoute("/vrijwilliger")({
  head: () => ({
    meta: [
      { title: "Vrijwilliger worden — Openluchtfilmfestival Apeldoorn" },
      { name: "description", content: "Word vrijwilliger op het Openluchtfilmfestival in Stadspark Berg & Bos, 14 — 22 augustus 2026." },
      { property: "og:title", content: "Vrijwilliger worden — Openluchtfilmfestival Apeldoorn" },
      { property: "og:description", content: "Help mee tijdens negen zomeravonden openluchtbioscoop." },
    ],
  }),
  component: VolunteerPage,
});

const AREAS = [
  { id: "ontvangst", label: "Ontvangst" },
  { id: "begeleiding", label: "Begeleiding op locatie" },
  { id: "activiteitenbegeleider", label: "Activiteitenbegeleider" },
  { id: "horeca", label: "Horeca" },
];

const FESTIVAL_DATES: { iso: string; label: string }[] = [
  { iso: "2026-08-14", label: "vr 14 aug" },
  { iso: "2026-08-15", label: "za 15 aug" },
  { iso: "2026-08-16", label: "zo 16 aug" },
  { iso: "2026-08-17", label: "ma 17 aug" },
  { iso: "2026-08-18", label: "di 18 aug" },
  { iso: "2026-08-19", label: "wo 19 aug" },
  { iso: "2026-08-20", label: "do 20 aug" },
  { iso: "2026-08-21", label: "vr 21 aug" },
  { iso: "2026-08-22", label: "za 22 aug" },
];

const schema = z
  .object({
  name: z.string().trim().min(1, "Vul je naam in").max(100),
  email: z.string().trim().email("Vul een geldig e-mailadres in").max(320),
  phone: z.string().trim().min(5, "Vul je telefoonnummer in").max(40),
    all_nights: z.boolean(),
    selected_dates: z.array(z.string()).max(9),
    helps_setup: z.boolean(),
    helps_teardown: z.boolean(),
    preference_note: z.string().trim().max(500).optional(),
  areas: z.array(z.string()).min(1, "Kies minimaal één onderdeel"),
  })
  .refine(
    (d) => d.all_nights || d.selected_dates.length > 0 || d.helps_setup || d.helps_teardown,
    { message: "Kies minimaal één avond, opbouw of afbouw.", path: ["selected_dates"] },
  );

function VolunteerPage() {
  const [submitting, setSubmitting] = useState(false);
  const [allNights, setAllNights] = useState(false);
  const [dates, setDates] = useState<string[]>([]);
  const [setup, setSetup] = useState(false);
  const [teardown, setTeardown] = useState(false);
  const [areas, setAreas] = useState<string[]>([]);

  function toggleArea(id: string, checked: boolean) {
    setAreas((prev) => (checked ? [...prev, id] : prev.filter((a) => a !== id)));
  }

  function toggleDate(iso: string, checked: boolean) {
    setDates((prev) => (checked ? [...prev, iso] : prev.filter((d) => d !== iso)));
  }

  function toggleAllNights(checked: boolean) {
    setAllNights(checked);
    if (checked) setDates(FESTIVAL_DATES.map((d) => d.iso));
    else setDates([]);
  }

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    const payload = {
      name: String(fd.get("name") ?? ""),
      email: String(fd.get("email") ?? ""),
      phone: String(fd.get("phone") ?? ""),
      all_nights: allNights,
      selected_dates: dates,
      helps_setup: setup,
      helps_teardown: teardown,
      preference_note: String(fd.get("preference_note") ?? "").trim() || undefined,
      areas,
    };
    const parsed = schema.safeParse(payload);
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Controleer het formulier");
      return;
    }
    setSubmitting(true);
    const { error } = await supabase.from("volunteers").insert({
      name: parsed.data.name,
      email: parsed.data.email,
      phone: parsed.data.phone,
      all_nights: parsed.data.all_nights,
      selected_dates: parsed.data.selected_dates,
      helps_setup: parsed.data.helps_setup,
      helps_teardown: parsed.data.helps_teardown,
      preference_note: parsed.data.preference_note ?? null,
      areas: parsed.data.areas,
    });
    setSubmitting(false);
    if (error) {
      toast.error("Er ging iets mis. Probeer het opnieuw.");
      return;
    }
    toast.success("Bedankt! We nemen snel contact met je op.");
    (e.target as HTMLFormElement).reset();
    setAreas([]);
    setDates([]);
    setAllNights(false);
    setSetup(false);
    setTeardown(false);
  }

  return (
    <I18nProvider lang="nl">
      <div className="min-h-screen bg-background">
        <Nav />
        <main className="pt-28 pb-24 md:pt-36">
          <section className="mx-auto max-w-3xl px-5 md:px-8">
            <div className="mb-10">
              <p className="font-mono-cinema text-[10px] text-gold">— Doe mee</p>
              <h1 className="mt-3 font-display text-4xl font-medium leading-[1.05] md:text-6xl">
                Vrijwilliger <span className="italic text-gold">worden?</span>
              </h1>
              <p className="mt-5 max-w-xl text-foreground/75">
                Help mee om negen zomeravonden openluchtbioscoop mogelijk te maken in Stadspark Berg &amp; Bos.
                Laat je gegevens achter en geef aan wanneer en waar je wilt helpen.
              </p>
            </div>

            <form
              onSubmit={onSubmit}
              className="film-grain space-y-7 rounded-3xl border border-border bg-gradient-to-br from-night to-night-deep p-6 shadow-soft md:p-10"
            >
              <div className="grid gap-5 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-xs uppercase tracking-widest text-foreground/75">Naam</Label>
                  <Input id="name" name="name" required className="h-12 border-border bg-night-deep/60" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-widest text-foreground/75">E-mailadres</Label>
                  <Input id="email" name="email" type="email" required className="h-12 border-border bg-night-deep/60" />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="phone" className="text-xs uppercase tracking-widest text-foreground/75">Telefoonnummer</Label>
                  <Input id="phone" name="phone" type="tel" required className="h-12 border-border bg-night-deep/60" />
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-xs uppercase tracking-widest text-foreground/75">Beschikbaarheid</p>
                <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-night-deep/40 p-4 hover:border-gold/50">
                  <Checkbox
                    checked={allNights}
                    onCheckedChange={(c) => toggleAllNights(Boolean(c))}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-medium">Ik ben alle 9 avonden beschikbaar</div>
                    <div className="text-sm text-foreground/75">14 t/m 22 augustus 2026</div>
                  </div>
                </label>
                <div className="grid gap-3 md:grid-cols-2">
                  {FESTIVAL_DATES.map((d) => (
                    <label
                      key={d.iso}
                      className={`flex items-center gap-3 rounded-xl border border-border bg-night-deep/40 p-4 ${
                        allNights ? "cursor-not-allowed opacity-50" : "cursor-pointer hover:border-gold/50"
                      }`}
                    >
                      <Checkbox
                        checked={dates.includes(d.iso)}
                        disabled={allNights}
                        onCheckedChange={(c) => toggleDate(d.iso, Boolean(c))}
                      />
                      <span className="text-sm">{d.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-xs uppercase tracking-widest text-foreground/75">Op- en afbouw (optioneel)</p>
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-night-deep/40 p-4 hover:border-gold/50">
                    <Checkbox checked={setup} onCheckedChange={(c) => setSetup(Boolean(c))} className="mt-1" />
                    <div>
                      <div className="font-medium">Opbouw</div>
                      <div className="text-sm text-foreground/75">Datum n.t.b., vóór 14 aug</div>
                    </div>
                  </label>
                  <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-night-deep/40 p-4 hover:border-gold/50">
                    <Checkbox checked={teardown} onCheckedChange={(c) => setTeardown(Boolean(c))} className="mt-1" />
                    <div>
                      <div className="font-medium">Afbouw</div>
                      <div className="text-sm text-foreground/75">Datum n.t.b., na 22 aug</div>
                    </div>
                  </label>
                </div>
              </div>

              <div className="space-y-3">
                <p className="text-xs uppercase tracking-widest text-foreground/75">Waar wil je helpen?</p>
                <div className="grid gap-3 md:grid-cols-2">
                  {AREAS.map((a) => (
                    <label key={a.id} className="flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-night-deep/40 p-4 hover:border-gold/50">
                      <Checkbox
                        checked={areas.includes(a.id)}
                        onCheckedChange={(c) => toggleArea(a.id, Boolean(c))}
                      />
                      <span className="text-sm">{a.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="preference_note" className="text-xs uppercase tracking-widest text-foreground/75">
                  Waar wil je het liefst bij helpen? <span className="normal-case text-foreground/50">(optioneel)</span>
                </Label>
                <Textarea
                  id="preference_note"
                  name="preference_note"
                  rows={3}
                  maxLength={500}
                  className="border-border bg-night-deep/60"
                />
              </div>

              <Button type="submit" variant="hero" size="xl" disabled={submitting} className="w-full sm:w-auto">
                {submitting ? "Een moment…" : "Aanmelden als vrijwilliger"}
              </Button>
            </form>
          </section>
        </main>
        <Footer />
        <Toaster position="top-center" />
      </div>
    </I18nProvider>
  );
}